import {
	type Envelope,
	type ErrorCode,
	type RefreshResponse,
	isCredentialVerdict,
} from "@seagames/contracts";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "../auth/token-store";
import { reportOutcome } from "./connection";

/**
 * The WSL Notion API client. Copied from apps/ovr (itself from apps/gms); the rules
 * are the same and are the whole point:
 *
 *   - Bearer only. Cookies are never sent.
 *   - An expired access token is refreshed ONCE, single-flight across every
 *     concurrent call (and across tabs via the Web Locks API), then the call
 *     is retried once.
 *   - Only a credential VERDICT from the server (`isCredentialVerdict`) signs
 *     the operator out. A network drop is `NETWORK`, a dependency failure is
 *     `SERVICE_UNAVAILABLE` — both keep the tokens and the unsaved edits.
 *     A venue Wi-Fi blip must never look like "you were logged out".
 */

function apiBase(): string {
	const raw = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3001/api";
	const trimmed = raw.replace(/\/+$/, "");
	// apps/gms's VITE_API_URL already ends in /api; accept either spelling.
	return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
}

const BASE_URL = apiBase();

/** The API root (ends in /api) — for the one call that cannot go through `api()`: the keepalive save on unload. */
export const API_BASE_URL = BASE_URL;

export class ApiError extends Error {
	/** 0 for a network failure. For logging only — branch on `code`, never on `status`. */
	readonly status: number;
	readonly code: ErrorCode | "NETWORK";
	readonly details: Record<string, unknown> | null;
	readonly requestId: string | null;

	constructor(
		code: ErrorCode | "NETWORK",
		status: number,
		message: string,
		details: Record<string, unknown> | null = null,
		requestId: string | null = null,
	) {
		super(message);
		this.name = "ApiError";
		this.code = code;
		this.status = status;
		this.details = details;
		this.requestId = requestId;
	}

	/** No answer (network) or a dependency did not answer (503). Keep everything, retry later. */
	get transient(): boolean {
		return this.code === "NETWORK" || this.code === "SERVICE_UNAVAILABLE";
	}

	/** The server ruled on the credential — this, and only this, signs the operator out. */
	get credentialVerdict(): boolean {
		return isCredentialVerdict(this.code);
	}

	/** Field errors from a 422, keyed by the snake_case field name. */
	get fieldErrors(): Record<string, string[]> {
		const raw = this.details?.["field_errors"];
		return (raw as Record<string, string[]> | undefined) ?? {};
	}
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface ApiOptions {
	query?: QueryParams;
	/** Serialised as JSON. Keys must already be snake_case. */
	body?: unknown;
	signal?: AbortSignal;
}

function buildUrl(path: string, query: QueryParams | undefined): string {
	let url = BASE_URL + (path.startsWith("/") ? path : `/${path}`);
	if (query) {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(query)) {
			if (value !== undefined && value !== null) params.set(key, String(value));
		}
		const qs = params.toString();
		if (qs) url += (url.includes("?") ? "&" : "?") + qs;
	}
	return url;
}

const isAbort = (err: unknown): boolean =>
	(err instanceof DOMException || err instanceof Error) && err.name === "AbortError";

// ── One attempt: no refresh, no retry ───────────────────────────────────────

async function rawRequest<T>(
	method: HttpMethod,
	path: string,
	opts: ApiOptions,
	withAuth: boolean,
): Promise<T> {
	const headers: Record<string, string> = { Accept: "application/json" };
	if (opts.body !== undefined) headers["Content-Type"] = "application/json";
	if (withAuth) {
		const token = getAccessToken();
		if (token) headers["Authorization"] = `Bearer ${token}`;
	}

	let response: Response;
	try {
		response = await fetch(buildUrl(path, opts.query), {
			method,
			headers,
			body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
			signal: opts.signal,
			credentials: "omit",
		});
	} catch (err) {
		// The caller's own abort is not a connection verdict.
		if (isAbort(err)) throw err;
		reportOutcome("offline");
		throw new ApiError("NETWORK", 0, "Can't reach the server. Your work is kept — it will be sent when the connection is back.");
	}

	const headerRequestId = response.headers.get("X-Request-Id");

	if (response.status === 204) {
		reportOutcome("online");
		return null as T;
	}

	let envelope: Envelope<T>;
	try {
		envelope = (await response.json()) as Envelope<T>;
	} catch (err) {
		if (isAbort(err)) throw err;
		// Not our envelope: a proxy's HTML 502, a truncated body. "No answer".
		reportOutcome("degraded");
		throw new ApiError(
			"SERVICE_UNAVAILABLE",
			response.status,
			"The server did not answer properly. Your session is still valid — please try again.",
			null,
			headerRequestId,
		);
	}

	if (envelope.success) {
		reportOutcome("online");
		return envelope.data;
	}

	const { code, message, details } = envelope.error;
	reportOutcome(code === "SERVICE_UNAVAILABLE" ? "degraded" : "online");
	throw new ApiError(code, response.status, message, details ?? null, envelope.request_id ?? headerRequestId);
}

// ── Refresh: single-flight in this tab, locked across tabs ──────────────────

let refreshInFlight: Promise<string> | null = null;

/** Per-origin, shared with nothing else: GMS has its own refresh token and lock. */
const REFRESH_LOCK = "seagames.notion.auth.refresh";
const LOCK_WAIT_MS = 15_000;

async function withRefreshLock(run: () => Promise<string>): Promise<string> {
	const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
	if (!locks) return run();

	const giveUp = new AbortController();
	let timedOut = false;
	const timer = setTimeout(() => {
		timedOut = true;
		giveUp.abort();
	}, LOCK_WAIT_MS);
	try {
		return await locks.request(REFRESH_LOCK, { signal: giveUp.signal }, run);
	} catch (err) {
		if (timedOut && isAbort(err)) return run();
		throw err;
	} finally {
		clearTimeout(timer);
	}
}

async function performRefresh(): Promise<string> {
	// Read INSIDE the lock: if another tab rotated while we waited, use its token.
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		throw new ApiError("TOKEN_MISSING", 401, "Your session has ended. Please sign in again.");
	}
	const result = await rawRequest<RefreshResponse>(
		"POST",
		"/auth/refresh",
		{ body: { refresh_token: refreshToken } },
		false,
	);
	setTokens({ accessToken: result.access_token, refreshToken: result.refresh_token }, { refreshToken });
	return result.access_token;
}

/** Exported for tests and for the session layer's restore-on-load. */
export function refreshAccessToken(): Promise<string> {
	refreshInFlight ??= withRefreshLock(performRefresh).finally(() => {
		refreshInFlight = null;
	});
	return refreshInFlight;
}

/** The ONLY place tokens are discarded because of a server answer. Asks the code, never the status. */
function handleAuthFailure(err: unknown): void {
	if (!(err instanceof ApiError)) return;
	if (!err.credentialVerdict) return;
	clearTokens(err.code === "SESSION_EXPIRED" ? "expired" : "revoked");
}

/** An access token the server would not take, but a refresh might fix. */
const shouldRefresh = (code: ApiError["code"]): boolean => code === "TOKEN_EXPIRED" || code === "TOKEN_INVALID";

/**
 * The one function the rest of the app calls.
 *
 * `path` is relative to /api (e.g. "/meets/123"). Resolves with the
 * envelope's `data`; rejects with `ApiError` (or the caller's AbortError).
 */
export async function api<T>(method: HttpMethod, path: string, opts: ApiOptions = {}): Promise<T> {
	// Fresh page load: the access token died with the last document but the
	// refresh token survived. Mint one first, or the API answers TOKEN_MISSING
	// — a verdict — and a reload would sign the operator out.
	if (!getAccessToken() && getRefreshToken()) {
		try {
			await refreshAccessToken();
		} catch (err) {
			handleAuthFailure(err);
			throw err;
		}
	}

	try {
		return await rawRequest<T>(method, path, opts, true);
	} catch (err) {
		if (!(err instanceof ApiError)) throw err;

		if (shouldRefresh(err.code) && getRefreshToken()) {
			try {
				await refreshAccessToken();
			} catch (refreshErr) {
				handleAuthFailure(refreshErr);
				throw refreshErr;
			}
			// Exactly one retry. A second verdict against a token minted
			// milliseconds ago is a real verdict.
			try {
				return await rawRequest<T>(method, path, opts, true);
			} catch (retryErr) {
				handleAuthFailure(retryErr);
				throw retryErr;
			}
		}

		handleAuthFailure(err);
		throw err;
	}
}

/**
 * For the endpoints reached WITHOUT a session — /auth/login, /auth/mfa/verify.
 * No bearer, no refresh, and no sign-out side effects: a failed login is an
 * answer about the form, not about a session.
 */
export function apiPublic<T>(method: HttpMethod, path: string, opts: ApiOptions = {}): Promise<T> {
	return rawRequest<T>(method, path, opts, false);
}
